/**
 * SidebarEditor – Drag & Drop editor for sidebar layout.
 * Admin-only. Opens as overlay inside sidebar.
 */
import React, { useState, useRef, useCallback } from "react";
import {
  GripVertical, Plus, Trash2, Check, X, Pencil, FolderPlus, RotateCcw, Save,
  ChevronUp, ChevronDown, ArrowUpDown, CornerUpRight,
} from "lucide-react";
import { toast } from "sonner";

/* ─── Types ─── */
export interface SidebarItemConfig {
  visId: string;
  customLabel?: string;
  hidden?: boolean;
}

export interface SidebarSubsectionConfig {
  id: string;
  label: string;
  items: SidebarItemConfig[];
}

export interface SidebarSectionConfig {
  id: string;
  label: string;
  items: SidebarItemConfig[];
  subsections?: SidebarSubsectionConfig[];
}

export interface SidebarLayoutConfig {
  sections: SidebarSectionConfig[];
}

interface Props {
  config: SidebarLayoutConfig;
  allItems: Record<string, { label: string; icon: any }>;
  onSave: (config: SidebarLayoutConfig) => void;
  onCancel: () => void;
  onReset: () => void;
}

/* ─── Main Editor ─── */
export default function SidebarEditor({ config, allItems, onSave, onCancel, onReset }: Props) {
  const [sections, setSections] = useState<SidebarSectionConfig[]>(
    JSON.parse(JSON.stringify(config.sections))
  );
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [editingItemLabel, setEditingItemLabel] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [dragItem, setDragItem] = useState<{ sectionIdx: number; itemIdx: number } | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<{ sectionIdx: number; itemIdx: number } | null>(null);
  const labelInputRef = useRef<HTMLInputElement>(null);

  /* ── Section operations ── */
  const renameSection = (idx: number, newLabel: string) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, label: newLabel } : s));
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    setSections(prev => {
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const removeSection = (idx: number) => {
    const s = sections[idx];
    const hasAny = s.items.length > 0 || (s.subsections || []).some(ss => ss.items.length > 0);
    if (hasAny) {
      toast.error("Gruppe muss leer sein zum Loeschen");
      return;
    }
    setSections(prev => prev.filter((_, i) => i !== idx));
  };

  const addSection = () => {
    if (!newGroupName.trim()) return;
    setSections(prev => [...prev, {
      id: `custom_${Date.now()}`,
      label: newGroupName.trim().toUpperCase(),
      items: [],
      subsections: [],
    }]);
    setNewGroupName("");
    setShowNewGroup(false);
  };

  /* ── Subsection operations ── */
  const addSubsection = (sIdx: number) => {
    const name = prompt("Name der Untergruppe:");
    if (!name?.trim()) return;
    setSections(prev => prev.map((s, i) => i === sIdx ? {
      ...s,
      subsections: [...(s.subsections || []), { id: `sub_${Date.now()}`, label: name.trim(), items: [] }],
    } : s));
  };

  const renameSubsection = (sIdx: number, subIdx: number, newLabel: string) => {
    setSections(prev => prev.map((s, i) => i === sIdx ? {
      ...s,
      subsections: (s.subsections || []).map((ss, ssi) => ssi === subIdx ? { ...ss, label: newLabel } : ss),
    } : s));
  };

  const removeSubsection = (sIdx: number, subIdx: number) => {
    const ss = sections[sIdx].subsections?.[subIdx];
    if (ss && ss.items.length > 0) {
      // Move items back to parent section
      if (!window.confirm(`Untergruppe "${ss.label}" wird geloescht. Die ${ss.items.length} Eintraege wandern zurueck in die Obergruppe. Fortfahren?`)) return;
    }
    setSections(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const sub = (s.subsections || [])[subIdx];
      const newItems = [...s.items, ...(sub?.items || [])];
      return {
        ...s,
        items: newItems,
        subsections: (s.subsections || []).filter((_, ssi) => ssi !== subIdx),
      };
    }));
  };

  /* ── Item operations ── */
  const toggleItemHidden = (sIdx: number, iIdx: number) => {
    setSections(prev => prev.map((s, si) =>
      si === sIdx ? {
        ...s,
        items: s.items.map((item, ii) => ii === iIdx ? { ...item, hidden: !item.hidden } : item),
      } : s
    ));
  };

  const renameItem = (sIdx: number, iIdx: number, newLabel: string) => {
    setSections(prev => prev.map((s, si) =>
      si === sIdx ? {
        ...s,
        items: s.items.map((item, ii) => ii === iIdx ? { ...item, customLabel: newLabel || undefined } : item),
      } : s
    ));
  };

  const moveItemToTarget = (fromPath: string, iIdx: number, toPath: string) => {
    const fromParts = fromPath.split(".");
    const toParts = toPath.split(".");
    const fromS = Number(fromParts[0]);
    const fromSub = fromParts.length > 1 ? Number(fromParts[1]) : null;
    const toS = Number(toParts[0]);
    const toSub = toParts.length > 1 ? Number(toParts[1]) : null;

    // Validate containers exist in current state (pre-check)
    const fromC = fromSub === null ? sections[fromS] : sections[fromS]?.subsections?.[fromSub];
    const toC = toSub === null ? sections[toS] : sections[toS]?.subsections?.[toSub];
    if (!fromC || !toC || !fromC.items[iIdx]) {
      toast.error("Verschieben fehlgeschlagen");
      return;
    }

    setSections(prev => {
      const arr = JSON.parse(JSON.stringify(prev));
      arr.forEach(s => { if (!Array.isArray(s.subsections)) s.subsections = []; });
      const fromContainer = fromSub === null ? arr[fromS] : arr[fromS]?.subsections?.[fromSub];
      const toContainer = toSub === null ? arr[toS] : arr[toS]?.subsections?.[toSub];
      if (!fromContainer || !toContainer) return prev;
      const [item] = fromContainer.items.splice(iIdx, 1);
      if (item) toContainer.items.push(item);
      return arr;
    });
    toast.success("Verschoben");
  };

  const moveItemToSection = (fromS: number, iIdx: number, toS: number) => {
    moveItemToTarget(String(fromS), iIdx, String(toS));
  };

  /* ── Drag & Drop (path-based: "sIdx" or "sIdx.subIdx") ── */
  const [dragPath, setDragPath] = useState<{ path: string; itemIdx: number } | null>(null);

  const handleDragStart = (sIdx: number, iIdx: number) => {
    setDragPath({ path: String(sIdx), itemIdx: iIdx });
    setDragItem({ sectionIdx: sIdx, itemIdx: iIdx });
  };

  const handleDragStartSub = (sIdx: number, subIdx: number, iIdx: number) => {
    setDragPath({ path: `${sIdx}.${subIdx}`, itemIdx: iIdx });
  };

  const handleDragOver = (e: React.DragEvent, sIdx: number, iIdx: number) => {
    e.preventDefault();
    setDragOverTarget({ sectionIdx: sIdx, itemIdx: iIdx });
  };

  const handleDragOverSection = (e: React.DragEvent, sIdx: number) => {
    e.preventDefault();
    setDragOverTarget({ sectionIdx: sIdx, itemIdx: -1 });
  };

  const handleDropToPath = (e: React.DragEvent, targetPath: string, targetIIdx: number) => {
    e.preventDefault();
    if (!dragPath) return;
    const fromParts = dragPath.path.split(".");
    const toParts = targetPath.split(".");
    const fromS = Number(fromParts[0]);
    const fromSub = fromParts.length > 1 ? Number(fromParts[1]) : null;
    const toS = Number(toParts[0]);
    const toSub = toParts.length > 1 ? Number(toParts[1]) : null;
    setSections(prev => {
      const arr = JSON.parse(JSON.stringify(prev));
      arr.forEach(s => { if (!Array.isArray(s.subsections)) s.subsections = []; });
      const fromContainer = fromSub === null ? arr[fromS] : arr[fromS]?.subsections?.[fromSub];
      const toContainer = toSub === null ? arr[toS] : arr[toS]?.subsections?.[toSub];
      if (!fromContainer || !toContainer) return prev;
      const [item] = fromContainer.items.splice(dragPath.itemIdx, 1);
      if (!item) return prev;
      const insertIdx = targetIIdx === -1 ? toContainer.items.length : targetIIdx;
      toContainer.items.splice(insertIdx, 0, item);
      return arr;
    });
    setDragPath(null);
    setDragItem(null);
    setDragOverTarget(null);
  };

  const handleDrop = (e: React.DragEvent, targetSIdx: number, targetIIdx: number) => {
    handleDropToPath(e, String(targetSIdx), targetIIdx);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragPath(null);
    setDragOverTarget(null);
  };

  /* ── Move item up/down within section ── */
  const moveItem = (sIdx: number, iIdx: number, dir: -1 | 1) => {
    const target = iIdx + dir;
    if (target < 0 || target >= sections[sIdx].items.length) return;
    setSections(prev => prev.map((s, si) => {
      if (si !== sIdx) return s;
      const items = [...s.items];
      [items[iIdx], items[target]] = [items[target], items[iIdx]];
      return { ...s, items };
    }));
  };

  const getItemLabel = (item: SidebarItemConfig) => {
    if (item.customLabel) return item.customLabel;
    return allItems[item.visId]?.label || item.visId;
  };

  return (
    <div className="flex flex-col h-full" data-testid="sidebar-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 shrink-0"
        style={{ borderBottom: "1px solid var(--tga-sb-border)" }}>
        <span className="text-[11px] font-bold" style={{ color: "var(--tga-sb-text-active)" }}>
          Sidebar bearbeiten
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onReset} className="p-1 rounded hover:bg-white/10" title="Zuruecksetzen"
            data-testid="sidebar-editor-reset">
            <RotateCcw className="w-3.5 h-3.5" style={{ color: "var(--tga-sb-text)" }} />
          </button>
          <button onClick={onCancel} className="p-1 rounded hover:bg-white/10" title="Abbrechen"
            data-testid="sidebar-editor-cancel">
            <X className="w-3.5 h-3.5" style={{ color: "var(--tga-sb-text)" }} />
          </button>
          <button onClick={() => onSave({ sections })}
            className="p-1 rounded bg-emerald-600/20 hover:bg-emerald-600/30" title="Speichern"
            data-testid="sidebar-editor-save">
            <Save className="w-3.5 h-3.5 text-emerald-400" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto py-1">
        {sections.map((section, sIdx) => (
          <div key={section.id}
            onDragOver={(e) => handleDragOverSection(e, sIdx)}
            onDrop={(e) => handleDrop(e, sIdx, -1)}
          >
            {/* Section header */}
            <div className="flex items-center gap-1 px-2 pt-2 pb-1 group">
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0}
                  className="p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 disabled:opacity-20 transition-opacity">
                  <ChevronUp className="w-3 h-3" style={{ color: "var(--tga-sb-text)" }} />
                </button>
                <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1}
                  className="p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 disabled:opacity-20 transition-opacity">
                  <ChevronDown className="w-3 h-3" style={{ color: "var(--tga-sb-text)" }} />
                </button>
              </div>

              {editingLabel === section.id ? (
                <input
                  ref={labelInputRef}
                  autoFocus
                  className="flex-1 bg-transparent text-[9px] font-bold tracking-[0.1em] uppercase outline-none px-1 py-0.5 rounded"
                  style={{ color: "var(--tga-sb-label)", border: "1px solid var(--tga-sb-border)" }}
                  value={section.label}
                  onChange={(e) => renameSection(sIdx, e.target.value)}
                  onBlur={() => setEditingLabel(null)}
                  onKeyDown={(e) => { if (e.key === "Enter") setEditingLabel(null); }}
                  data-testid={`section-label-input-${sIdx}`}
                />
              ) : (
                <button
                  type="button"
                  className="flex-1 flex items-center gap-1.5 text-[10px] font-bold tracking-[0.1em] uppercase cursor-pointer select-none hover:text-white transition-colors text-left"
                  style={{ color: "var(--tga-sb-label)" }}
                  onClick={() => setEditingLabel(section.id)}
                  data-testid={`section-label-${sIdx}`}
                  title="Klicken zum Umbenennen">
                  <span className="truncate">{section.label}</span>
                  <Pencil className="w-2.5 h-2.5 opacity-40" />
                </button>
              )}

              <button onClick={() => addSubsection(sIdx)}
                className="p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                title="Untergruppe hinzufuegen"
                data-testid={`add-subsection-${sIdx}`}>
                <FolderPlus className="w-3 h-3" style={{ color: "var(--tga-sb-text)" }} />
              </button>
              <button onClick={() => removeSection(sIdx)}
                className="p-0.5 rounded opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                title="Gruppe loeschen">
                <Trash2 className="w-3 h-3 text-red-400" />
              </button>
            </div>

            {/* Items */}
            {section.items.map((item, iIdx) => {
              const isDragging = dragItem?.sectionIdx === sIdx && dragItem?.itemIdx === iIdx;
              const isDragOver = dragOverTarget?.sectionIdx === sIdx && dragOverTarget?.itemIdx === iIdx;
              const ItemIcon = allItems[item.visId]?.icon;

              return (
                <div
                  key={`${section.id}-${item.visId}`}
                  draggable
                  onDragStart={() => handleDragStart(sIdx, iIdx)}
                  onDragOver={(e) => handleDragOver(e, sIdx, iIdx)}
                  onDrop={(e) => handleDrop(e, sIdx, iIdx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-1 mx-1.5 my-0.5 px-1.5 py-[5px] rounded-lg text-[11px] cursor-grab active:cursor-grabbing transition-all ${
                    isDragging ? "opacity-30" : ""
                  } ${isDragOver ? "ring-1 ring-blue-400/50" : ""} ${item.hidden ? "opacity-40" : ""}`}
                  style={{ backgroundColor: isDragOver ? "rgba(59,130,246,0.1)" : undefined }}
                  data-testid={`editor-item-${item.visId}`}
                >
                  <GripVertical className="w-3 h-3 flex-shrink-0 opacity-40" style={{ color: "var(--tga-sb-text)" }} />

                  {ItemIcon && <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: item.hidden ? "var(--tga-sb-icon-dim)" : "var(--tga-sb-text)" }} />}

                  {editingItemLabel === `${sIdx}-${iIdx}` ? (
                    <input
                      autoFocus
                      className="flex-1 bg-transparent text-[11px] outline-none px-1 py-0.5 rounded min-w-0"
                      style={{ color: "var(--tga-sb-text)", border: "1px solid var(--tga-sb-border)" }}
                      value={item.customLabel || allItems[item.visId]?.label || ""}
                      onChange={(e) => renameItem(sIdx, iIdx, e.target.value)}
                      onBlur={() => setEditingItemLabel(null)}
                      onKeyDown={(e) => { if (e.key === "Enter") setEditingItemLabel(null); }}
                      data-testid={`item-label-input-${item.visId}`}
                    />
                  ) : (
                    <span className="flex-1 truncate select-none" style={{ color: item.hidden ? "var(--tga-sb-icon-dim)" : "var(--tga-sb-text)" }}>
                      {getItemLabel(item)}
                    </span>
                  )}

                  <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100" style={{ opacity: 1 }}>
                    <button onClick={() => setEditingItemLabel(`${sIdx}-${iIdx}`)}
                      className="p-0.5 rounded hover:bg-white/10" title="Umbenennen">
                      <Pencil className="w-2.5 h-2.5" style={{ color: "var(--tga-sb-text)" }} />
                    </button>
                    <button onClick={() => moveItem(sIdx, iIdx, -1)} disabled={iIdx === 0}
                      className="p-0.5 rounded hover:bg-white/10 disabled:opacity-20">
                      <ChevronUp className="w-2.5 h-2.5" style={{ color: "var(--tga-sb-text)" }} />
                    </button>
                    <button onClick={() => moveItem(sIdx, iIdx, 1)} disabled={iIdx === section.items.length - 1}
                      className="p-0.5 rounded hover:bg-white/10 disabled:opacity-20">
                      <ChevronDown className="w-2.5 h-2.5" style={{ color: "var(--tga-sb-text)" }} />
                    </button>
                    <button onClick={() => toggleItemHidden(sIdx, iIdx)}
                      className="p-0.5 rounded hover:bg-white/10" title={item.hidden ? "Einblenden" : "Ausblenden"}>
                      {item.hidden ? (
                        <Check className="w-2.5 h-2.5 text-green-400" />
                      ) : (
                        <X className="w-2.5 h-2.5 text-red-400" />
                      )}
                    </button>

                    {/* Move to other section/subsection */}
                    {(sections.length > 1 || sections.some(s => (s.subsections || []).length > 0)) && (
                      <select
                        className="w-6 h-5 px-0 rounded bg-white/5 hover:bg-white/20 text-center cursor-pointer border-0 outline-none appearance-none transition-colors flex-shrink-0"
                        style={{ color: "var(--tga-sb-text)", fontSize: 10 }}
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          e.target.value = "";
                          if (val) moveItemToTarget(String(sIdx), iIdx, val);
                        }}
                        title="In andere Gruppe verschieben"
                        data-testid={`move-item-${item.visId}`}
                      >
                        <option value="">↗</option>
                        {sections.map((s, si) => (
                          <React.Fragment key={si}>
                            {si !== sIdx && <option value={String(si)}>→ {s.label}</option>}
                            {(s.subsections || []).map((ss, ssi) => (
                              <option key={`${si}.${ssi}`} value={`${si}.${ssi}`}>→ {s.label} › {ss.label}</option>
                            ))}
                          </React.Fragment>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Drop zone indicator for empty sections */}
            {section.items.length === 0 && !(section.subsections || []).length && (
              <div className="mx-3 my-2 py-3 border border-dashed rounded-lg text-center text-[10px] opacity-40"
                style={{ borderColor: "var(--tga-sb-border)", color: "var(--tga-sb-text)" }}
                onDragOver={(e) => handleDragOverSection(e, sIdx)}
                onDrop={(e) => handleDrop(e, sIdx, -1)}>
                Hierher ziehen
              </div>
            )}

            {/* Subsections */}
            {(section.subsections || []).map((sub, subIdx) => (
              <div key={sub.id} className="ml-3 mt-1 mb-1 pl-1 border-l" style={{ borderColor: "var(--tga-sb-border)" }}>
                <div
                  className="flex items-center gap-1 px-2 py-1 group/sub rounded"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDropToPath(e, `${sIdx}.${subIdx}`, -1)}
                  title="Hierher ziehen zum Einfügen"
                >
                  {editingLabel === sub.id ? (
                    <input
                      autoFocus
                      className="flex-1 bg-transparent text-[10px] font-semibold outline-none px-1 py-0.5 rounded"
                      style={{ color: "var(--tga-sb-text)", border: "1px solid var(--tga-sb-border)" }}
                      value={sub.label}
                      onChange={(e) => renameSubsection(sIdx, subIdx, e.target.value)}
                      onBlur={() => setEditingLabel(null)}
                      onKeyDown={(e) => { if (e.key === "Enter") setEditingLabel(null); }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="flex-1 flex items-center gap-1 text-[10px] font-semibold cursor-pointer select-none hover:text-white transition-colors text-left"
                      style={{ color: "var(--tga-sb-text)" }}
                      onClick={() => setEditingLabel(sub.id)}
                      title="Klicken zum Umbenennen">
                      <span className="truncate">{sub.label}</span>
                      <Pencil className="w-2 h-2 opacity-40" />
                    </button>
                  )}
                  <button onClick={() => removeSubsection(sIdx, subIdx)}
                    className="p-0.5 rounded opacity-0 group-hover/sub:opacity-60 hover:!opacity-100 transition-opacity"
                    title="Untergruppe loeschen">
                    <Trash2 className="w-2.5 h-2.5 text-red-400" />
                  </button>
                </div>
                {/* Subsection items (drag&drop enabled) */}
                {sub.items.map((item, iIdx) => {
                  const ItemIcon = allItems[item.visId]?.icon;
                  const isDragging = dragPath?.path === `${sIdx}.${subIdx}` && dragPath?.itemIdx === iIdx;
                  return (
                    <div key={`${sub.id}-${item.visId}`}
                      draggable
                      onDragStart={() => handleDragStartSub(sIdx, subIdx, iIdx)}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => handleDropToPath(e, `${sIdx}.${subIdx}`, iIdx)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-1 mx-1 my-0.5 px-1.5 py-1 rounded-lg text-[11px] cursor-grab active:cursor-grabbing transition-all ${isDragging ? "opacity-30" : ""} ${item.hidden ? "opacity-40" : ""}`}
                      data-testid={`editor-sub-item-${item.visId}`}>
                      <GripVertical className="w-2.5 h-2.5 flex-shrink-0 opacity-40" style={{ color: "var(--tga-sb-text)" }} />
                      {ItemIcon && <ItemIcon className="w-3 h-3 flex-shrink-0" style={{ color: "var(--tga-sb-text)" }} />}
                      <span className="flex-1 truncate" style={{ color: "var(--tga-sb-text)" }}>
                        {getItemLabel(item)}
                      </span>
                      <select
                        className="w-6 h-5 px-0 rounded bg-white/5 hover:bg-white/20 text-center cursor-pointer border-0 outline-none appearance-none transition-colors flex-shrink-0"
                        style={{ color: "var(--tga-sb-text)", fontSize: 10 }}
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          e.target.value = "";
                          if (val) moveItemToTarget(`${sIdx}.${subIdx}`, iIdx, val);
                        }}
                        title="In andere Gruppe verschieben"
                      >
                        <option value="">↗</option>
                        {sections.map((s, si) => (
                          <React.Fragment key={si}>
                            <option value={String(si)}>→ {s.label}</option>
                            {(s.subsections || []).map((ss, ssi) =>
                              si === sIdx && ssi === subIdx ? null : (
                                <option key={`${si}.${ssi}`} value={`${si}.${ssi}`}>→ {s.label} › {ss.label}</option>
                              )
                            )}
                          </React.Fragment>
                        ))}
                      </select>
                      <button onClick={() => {
                        setSections(prev => prev.map((s, si) => si !== sIdx ? s : {
                          ...s,
                          subsections: (s.subsections || []).map((ss, ssi) => ssi !== subIdx ? ss : {
                            ...ss,
                            items: ss.items.map((it, ii) => ii === iIdx ? { ...it, hidden: !it.hidden } : it),
                          }),
                        }));
                      }}
                        className="p-0.5 rounded hover:bg-white/10" title={item.hidden ? "Einblenden" : "Ausblenden"}>
                        {item.hidden ? <Check className="w-2.5 h-2.5 text-green-400" /> : <X className="w-2.5 h-2.5 text-red-400" />}
                      </button>
                    </div>
                  );
                })}
                {sub.items.length === 0 && (
                  <div
                    className="mx-1 my-1 py-1.5 border border-dashed rounded text-center text-[9px] opacity-40"
                    style={{ borderColor: "var(--tga-sb-border)", color: "var(--tga-sb-text)" }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDropToPath(e, `${sIdx}.${subIdx}`, -1)}
                  >
                    Hierher ziehen
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Add new group */}
        <div className="px-2 pt-3 pb-2">
          {showNewGroup ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                placeholder="Gruppenname..."
                className="flex-1 bg-transparent text-[10px] outline-none px-2 py-1.5 rounded"
                style={{ color: "var(--tga-sb-text)", border: "1px solid var(--tga-sb-border)" }}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addSection(); if (e.key === "Escape") setShowNewGroup(false); }}
                data-testid="new-group-input"
              />
              <button onClick={addSection} className="p-1 rounded bg-emerald-600/20" data-testid="new-group-confirm">
                <Check className="w-3 h-3 text-emerald-400" />
              </button>
              <button onClick={() => setShowNewGroup(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-3 h-3" style={{ color: "var(--tga-sb-text)" }} />
              </button>
            </div>
          ) : (
            <button onClick={() => setShowNewGroup(true)}
              className="flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-2 rounded-lg w-full hover:bg-white/10 border border-dashed transition-colors"
              style={{ color: "var(--tga-sb-text)", borderColor: "var(--tga-sb-border)" }}
              data-testid="add-group-btn">
              <FolderPlus className="w-3.5 h-3.5" /> Neue Gruppe anlegen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
